import type { Breadcrumb, Event, Integration } from '@sentry/core';

/**
 * Keeps credentials and search terms out of the URLs the browser SDK sends,
 * while leaving support enough to find the applicant an event is about.
 *
 * The SDK copies `window.location.href` onto every event, names pageload and
 * navigation transactions after the path, and records request URLs on
 * breadcrumbs and spans. On the applicant portal the path is the credential:
 * `/apply/:accountSlug/applications/:applicantExternalId` opens the portal, I-9
 * documents included, for whoever holds it. Query strings carry recruiter
 * search terms, which are candidate names, and any token a link was sent with.
 *
 * A credential id in a URL keeps its first 12 hex characters. The ids are
 * UUIDv4 (`uuid_generate_v4()` in hire), whose version nibble is the 13th hex
 * character, so those 12 are random: 48 bits that pin one applicant within an
 * account, and 74 unknown random bits that leave the URL useless as a key.
 * Support finds the applicant with
 * `WHERE external_id::text LIKE '6c1e0d5e-0000%'` scoped to the account slug
 * the URL keeps, or searches Sentry for `applicant.ref:6c1e0d5e0000`.
 */

const FILTERED = '[Filtered]';
const REF_LENGTH = 12;
const UUID =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

// The tag every event on the applicant portal carries.
export const APPLICANT_REF_TAG = 'applicant.ref';

type Segment = { pattern: RegExp; placeholder: string };

// Each segment authenticates on its own. `:rest` of `remoteI9Authorization` is
// the I-9 form's external id, a UUIDv4 like the applicant's and a credential
// for the remote authorization page, so it gets the same treatment.
const CREDENTIAL_SEGMENTS: readonly Segment[] = [
  {
    pattern: /(\/applications\/)([^/?#\s]+)/g,
    placeholder: ':applicantExternalId',
  },
  {
    pattern: /(\/remoteI9Authorization\/)([^/?#\s]+)(?:\/[^?#\s]*)?/g,
    placeholder: ':i9FormExternalId',
  },
  // The remote I-9 page's own API calls carry the same id.
  {
    pattern: /(\/authorized_representative_forms\/)([^/?#\s]+)/g,
    placeholder: ':i9FormExternalId',
  },
  { pattern: /(\/i9_forms\/)([^/?#\s]+)/g, placeholder: ':i9FormExternalId' },
];

const APPLICANT_SEGMENT = /\/applications\/([^/?#\s]+)/;
const QUERY = /\?([^#\s]*)/g;
// Only a fragment shaped like parameters: `#root` in a CSS selector stays.
const PARAMETER_FRAGMENT = /#([^#\s]*=[^#\s]*)/g;

// An id already cut to its ref. A breadcrumb is scrubbed when it is recorded
// and again with the event that carries it.
const SCRUBBED_REF = /^([0-9a-f]{12})…$/i;

/** The first 12 hex characters of a UUID, or nothing for any other shape. */
const refOf = (id: string): string | undefined => {
  if (UUID.test(id)) {
    return id.replace(/-/g, '').slice(0, REF_LENGTH).toLowerCase();
  }
  return SCRUBBED_REF.exec(id)?.[1].toLowerCase();
};

// A bare segment with no `=` may be the token itself, so it goes whole.
const redactParameters = (parameters: string): string =>
  parameters
    .split('&')
    .map(parameter => {
      const equals = parameter.indexOf('=');
      if (equals === -1) {
        return parameter && FILTERED;
      }
      return equals === parameter.length - 1
        ? parameter
        : `${parameter.slice(0, equals)}=${FILTERED}`;
    })
    .join('&');

const scrubCredentials = (value: string, keepRef: boolean): string =>
  CREDENTIAL_SEGMENTS.reduce(
    (scrubbed, { pattern, placeholder }) =>
      scrubbed.replace(pattern, (_, prefix: string, id: string) => {
        const ref = keepRef ? refOf(id) : undefined;
        return `${prefix}${ref ? `${ref}…` : placeholder}`;
      }),
    value,
  );

const scrub = (value: string, keepRef: boolean): string =>
  scrubCredentials(value, keepRef)
    .replace(QUERY, (_, query: string) => `?${redactParameters(query)}`)
    .replace(
      PARAMETER_FRAGMENT,
      (_, fragment: string) => `#${redactParameters(fragment)}`,
    );

/**
 * For a URL an event records: a credential id keeps its 12-character ref,
 * `/applications/6c1e0d5e0000…`, and every query-string value goes.
 */
export const scrubUrl = (value: string): string => scrub(value, true);

/**
 * For a transaction name or span description: a credential id becomes its
 * route parameter, `/applications/:applicantExternalId`. A per-applicant value
 * there would make every applicant a transaction group of their own.
 */
export const parameterizeUrl = (value: string): string => scrub(value, false);

/** The `applicant.ref` of a portal URL, if it names an applicant. */
export const applicantRef = (url: string): string | undefined => {
  const match = APPLICANT_SEGMENT.exec(url);
  return match ? refOf(match[1]) : undefined;
};

// Mutates in place: the SDK hands over objects it owns and reads them back.
const scrubValues = (
  draftRecord: Record<string, unknown> | undefined,
): void => {
  if (!draftRecord) {
    return;
  }
  Object.keys(draftRecord).forEach(key => {
    const value = draftRecord[key];
    if (typeof value === 'string') {
      draftRecord[key] = scrubUrl(value);
    }
  });
};

// The free-text attributes the SDK copies into a click's CSS selector. A
// component that puts a candidate's name in `aria-label` would otherwise send
// it with every click.
const FREE_TEXT_ATTRIBUTE = /\[(aria-label|title|alt)="/g;
// The SDK does not escape a quote inside a value, so a value ends at the first
// `"]` that a selector part or the end of the string follows.
const VALUE_END = /"\](?=[[.#\s]|$)/g;

/**
 * Keeps the attribute's presence in a selector and drops its text. A value
 * with no recognizable end is dropped with everything after it.
 */
export const scrubSelector = (selector: string): string => {
  let scrubbed = '';
  let from = 0;
  FREE_TEXT_ATTRIBUTE.lastIndex = 0;
  for (
    let start = FREE_TEXT_ATTRIBUTE.exec(selector);
    start;
    start = FREE_TEXT_ATTRIBUTE.exec(selector)
  ) {
    VALUE_END.lastIndex = FREE_TEXT_ATTRIBUTE.lastIndex;
    const end = VALUE_END.exec(selector);
    scrubbed += `${selector.slice(from, start.index)}[${start[1]}="${FILTERED}"]`;
    if (!end) {
      return scrubbed;
    }
    from = VALUE_END.lastIndex;
    FREE_TEXT_ATTRIBUTE.lastIndex = from;
  }
  return scrubbed + selector.slice(from);
};

/**
 * `beforeBreadcrumb`. Runs before a breadcrumb reaches the scope and before
 * session replay records it, so neither holds the raw URL.
 *
 * Console breadcrumbs go whole. Their arguments are whatever the app logged,
 * an `ApiError` among them, whose `body` holds what a candidate typed into a
 * form. Dropping them here rather than leaving out the SDK's `Breadcrumbs`
 * console option keeps working if the SDK renames the option.
 */
export function scrubBreadcrumb(
  draftBreadcrumb: Breadcrumb,
): Breadcrumb | null {
  if (draftBreadcrumb.category === 'console') {
    return null;
  }
  if (draftBreadcrumb.message) {
    const message = draftBreadcrumb.category?.startsWith('ui.')
      ? scrubSelector(draftBreadcrumb.message)
      : draftBreadcrumb.message;
    draftBreadcrumb.message = scrubUrl(message);
  }
  scrubValues(draftBreadcrumb.data);
  return draftBreadcrumb;
}

// A bundle's own URL, the key its source map is found under.
// Anchored on the path, so a query value that ends in `.js` does not count.
const SCRIPT_ASSET = /^[^?#\s]*\.[cm]?js(?:[?#]|$)/;

/**
 * For a stack frame's file. The page URL, query included, becomes the frame's
 * file when a script inline in the page throws, an account's tracker snippet on
 * the portal among them, and when the browser reports an error with no stack
 * or file. A bundle's URL keeps its query and fragment, which source-map lookup
 * matches on, and loses only a credential segment.
 */
const scrubFrameUrl = (value: string): string =>
  SCRIPT_ASSET.test(value) ? scrubCredentials(value, true) : scrubUrl(value);

type AnyEvent = Event & { urls?: string[] };

const scrubEvent = (draftEvent: AnyEvent): AnyEvent => {
  // Read before the URL is scrubbed. The page URL is where the portal learns
  // the applicant, so every event made on it, a transaction or a replay
  // included, carries the ref from the moment the id is in the address bar.
  const ref = draftEvent.request?.url && applicantRef(draftEvent.request.url);
  if (ref && !draftEvent.tags?.[APPLICANT_REF_TAG]) {
    draftEvent.tags = { ...draftEvent.tags, [APPLICANT_REF_TAG]: ref };
  }

  if (draftEvent.request) {
    if (draftEvent.request.url) {
      draftEvent.request.url = scrubUrl(draftEvent.request.url);
    }
    const { headers } = draftEvent.request;
    if (headers) {
      delete headers.Cookie;
      delete headers.cookie;
    }
    // `Referer` is the page the visitor came from, often another portal URL.
    scrubValues(headers);
    delete draftEvent.request.cookies;
    delete draftEvent.request.data;
  }

  // A rejection with a plain object, a response body say, arrives serialized
  // here. The message still names its keys.
  if (draftEvent.extra) {
    delete draftEvent.extra.__serialized__;
  }

  // A message can quote the URL that failed. Only the credential segments go:
  // query redaction would rewrite a message's `?` and split its issue group.
  draftEvent.exception?.values?.forEach(draftValue => {
    if (draftValue.value) {
      draftValue.value = scrubCredentials(draftValue.value, false);
    }
    draftValue.stacktrace?.frames?.forEach(draftFrame => {
      if (draftFrame.filename) {
        draftFrame.filename = scrubFrameUrl(draftFrame.filename);
      }
      if (draftFrame.abs_path) {
        draftFrame.abs_path = scrubFrameUrl(draftFrame.abs_path);
      }
    });
  });

  if (draftEvent.transaction) {
    draftEvent.transaction = parameterizeUrl(draftEvent.transaction);
  }

  scrubValues(draftEvent.contexts?.trace?.data);

  const feedback = draftEvent.contexts?.feedback;
  if (typeof feedback?.url === 'string') {
    feedback.url = scrubUrl(feedback.url);
  }

  draftEvent.spans?.forEach(draftSpan => {
    if (draftSpan.description) {
      draftSpan.description = parameterizeUrl(draftSpan.description);
    }
    scrubValues(draftSpan.data);
  });

  if (draftEvent.breadcrumbs) {
    draftEvent.breadcrumbs = draftEvent.breadcrumbs.filter(
      breadcrumb => scrubBreadcrumb(breadcrumb) !== null,
    );
  }

  if (draftEvent.urls) {
    draftEvent.urls = draftEvent.urls.map(scrubUrl);
  }

  return draftEvent;
};

/**
 * Scrubs every event type on its way out: errors, transactions and their
 * spans, feedback and replay events. `beforeSend` sees errors only and
 * `beforeSendTransaction` transactions only; an event processor sees all four,
 * after the SDK has attached the page URL.
 *
 * Besides URLs, it drops the carriers of free text a request or response can
 * put on an event: cookies, a request body, a serialized rejection, and
 * console breadcrumbs.
 */
export const scrubUrlsIntegration = (): Integration => ({
  name: 'ScrubUrls',
  processEvent: scrubEvent,
});
